import moment from 'moment-timezone';

import { mapDriverActivities } from 'utils/crewTimelineActivities';

const toDayStart = (dayMoment, timezone) => moment(dayMoment).tz(timezone).startOf('day');
const toDayEnd = (dayMoment, timezone) => moment(dayMoment).tz(timezone).endOf('day');

const overlapsDay = ({ fromTs, toTs, dayStart, dayEnd }) => {
	const from = moment(fromTs);
	const to = moment(toTs);
	return from.isBefore(dayEnd) && to.isAfter(dayStart);
};

const getRecordRootKey = (record) =>
	record.rootKey ?? record.key ?? `${record.fromTimestamp}-${record.toTimestamp}`;

const sliceRecordByDay = ({ record, dayIndex, dayStart, dayEnd }) => {
	if (!record?.fromTimestamp || !record?.toTimestamp) return null;
	if (!overlapsDay({ fromTs: record.fromTimestamp, toTs: record.toTimestamp, dayStart, dayEnd }))
		return null;

	const rootKey = getRecordRootKey(record);
	const from = moment(record.fromTimestamp);
	const to = moment(record.toTimestamp);
	const isStartInTimeWindow = from.isSameOrAfter(dayStart) && from.isBefore(dayEnd);
	const isEndInTimeWindow = to.isAfter(dayStart) && to.isSameOrBefore(dayEnd);

	return {
		...record,
		rootKey,
		key: `${rootKey}__day${dayIndex}`,
		isStartInTimeWindow,
		isEndInTimeWindow,
	};
};

const chunkActivitiesByDay = (activities, weekDays, timezone) => {
	const buckets = weekDays.map(() => []);

	weekDays.forEach((day, index) => {
		const dayStart = toDayStart(day, timezone);
		const dayEnd = toDayEnd(day, timezone);

		activities.forEach((activity) => {
			const slicedActivity = sliceRecordByDay({
				record: activity,
				dayIndex: index,
				dayStart,
				dayEnd,
			});
			if (!slicedActivity) return;

			if (activity.isShiftGroup) {
				const children = (activity.children ?? [])
					.map((child) => sliceRecordByDay({ record: child, dayIndex: index, dayStart, dayEnd }))
					.filter(Boolean);

				if (children.length === 0) return;

				buckets[index].push({
					...slicedActivity,
					children,
				});
				return;
			}

			buckets[index].push(slicedActivity);
		});
	});

	return buckets;
};

const chunkShiftsByDay = (shifts, weekDays, timezone) => {
	const buckets = weekDays.map(() => []);

	if (!Array.isArray(shifts) || shifts.length === 0) {
		return buckets;
	}

	weekDays.forEach((day, index) => {
		const dayStart = toDayStart(day, timezone);
		const dayEnd = toDayEnd(day, timezone);

		shifts.forEach((shift) => {
			if (!shift?.id || !shift?.fromTimestamp || !shift?.toTimestamp) return;
			if (
				!overlapsDay({
					fromTs: shift.fromTimestamp,
					toTs: shift.toTimestamp,
					dayStart,
					dayEnd,
				})
			)
				return;

			const autoCloseMoment = shift.autoCloseTimestamp ? moment(shift.autoCloseTimestamp) : null;
			const autoCloseOnThisDay =
				autoCloseMoment &&
				autoCloseMoment.isSameOrAfter(dayStart) &&
				autoCloseMoment.isSameOrBefore(dayEnd);

			const from = moment(shift.fromTimestamp);
			const to = moment(shift.toTimestamp);
			const isStartInTimeWindow = from.isSameOrAfter(dayStart) && from.isBefore(dayEnd);
			const isEndInTimeWindow = to.isAfter(dayStart) && to.isSameOrBefore(dayEnd);

			buckets[index].push({
				...shift,
				key: `${shift.id}__day${index}`,
				autoCloseOnThisDay: Boolean(autoCloseOnThisDay),
				isStartInTimeWindow,
				isEndInTimeWindow,
			});
		});
	});

	return buckets;
};

export const mapPersonalTimeline = (driver, weekDays, timezone = moment.tz.guess()) => {
	if (!driver) {
		return {
			id: null,
			fullName: '',
			number: '',
			activities: weekDays.map(() => []),
			shifts: weekDays.map(() => []),
		};
	}

	const { activitiesV2 = [], shifts = [], id, fullName, driverCard } = driver;

	const mappedActivities = mapDriverActivities(activitiesV2, shifts, id, {
		groupByShift: true,
	});

	return {
		id,
		fullName,
		number: driverCard,
		activities: chunkActivitiesByDay(mappedActivities, weekDays, timezone),
		shifts: chunkShiftsByDay(shifts, weekDays, timezone),
	};
};
